import React, { useEffect, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    TextField,
    MenuItem,
    Button,
    Modal,
    Alert,
    Snackbar,
    CircularProgress,
    Pagination,
    Stack,
    TablePagination
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { Delete, Add, Edit } from "@mui/icons-material";
import axios from "axios";
import apiEndpoints from "../../apiconfig";

const ExpenseEntry = () => {
    const [expenses, setExpenses] = useState([]);
    const [allExpenses, setAllExpenses] = useState([]); // Store all expenses for frontend pagination
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);

    // Pagination states
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [paginationMode, setPaginationMode] = useState('backend'); // 'backend' or 'frontend'

    // Notification state
    const [notification, setNotification] = useState({
        open: false,
        message: "",
        severity: "success"
    });

    const [typeFilter, setTypeFilter] = useState("");
    const [modeFilter, setModeFilter] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");

    /* Expense categories */
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedStaff, setSelectedStaff] = useState(null);

    /* Staff */
    const [staffList, setStaffList] = useState([]);

    const token = localStorage.getItem("token");

    const axiosAuth = axios.create({
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
        }
    });


    const [form, setForm] = useState({
        entry_type: "",
        staff_guid: "",
        staff_name: "",
        expense_name: "",
        description: "",
        amount: "",
        mode: "cash",
        transaction_type: "out",
        payment_date: ""
    });

    const handleChange = (key, value) =>
        setForm({ ...form, [key]: value });

    const statusColor = (type) =>
        type === "in" ? "success" : "error";

    const showNotification = (message, severity = "success") => {
        setNotification({
            open: true,
            message,
            severity
        });
    };

    /* ===============================
       LOAD EXPENSE TRANSACTIONS
    =============================== */
    const loadExpenses = async () => {
        setLoading(true);
        try {
            let url = `${apiEndpoints.expense_transactions}?action=get`;

            // Add filters if provided
            const params = new URLSearchParams();
            if (typeFilter) params.append('type', typeFilter);
            if (modeFilter) params.append('mode', modeFilter);
            if (from) params.append('from', from);
            if (to) params.append('to', to);

            // Add pagination parameters if using backend pagination
            if (paginationMode === 'backend') {
                params.append('page', page);
                params.append('limit', rowsPerPage);
            }

            const queryString = params.toString();
            if (queryString) {
                url += `&${queryString}`;
            }

           const response = await axiosAuth.get(url);

            if (response.data.status === "success") {
                const data = response.data.data || [];

                if (paginationMode === 'backend') {
                    // For backend pagination, only show current page data
                    setExpenses(data);
                    setTotalCount(response.data.total || data.length);
                } else {
                    // For frontend pagination, store all data
                    setAllExpenses(data);
                    const startIndex = (page - 1) * rowsPerPage;
                    const endIndex = startIndex + rowsPerPage;
                    setExpenses(data.slice(startIndex, endIndex));
                    setTotalCount(data.length);
                }
            } else {
                showNotification(response.data.message || "Failed to load expenses", "error");
            }
        } catch (error) {
            console.error("Error loading expenses:", error);
            showNotification("Error loading expense transactions", "error");
        } finally {
            setLoading(false);
        }
    };

    /* ===============================
       HANDLE PAGE CHANGE
    =============================== */
    const handlePageChange = (event, newPage) => {
        setPage(newPage);

        if (paginationMode === 'frontend' && allExpenses.length > 0) {
            // For frontend pagination, slice the data
            const startIndex = (newPage - 1) * rowsPerPage;
            const endIndex = startIndex + rowsPerPage;
            setExpenses(allExpenses.slice(startIndex, endIndex));
        }
    };

    /* ===============================
       HANDLE ROWS PER PAGE CHANGE
    =============================== */
    const handleRowsPerPageChange = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(1); // Reset to first page

        if (paginationMode === 'frontend' && allExpenses.length > 0) {
            // For frontend pagination, slice the data
            const startIndex = 0;
            const endIndex = startIndex + newRowsPerPage;
            setExpenses(allExpenses.slice(startIndex, endIndex));
        }
    };

    /* ===============================
       LOAD EXPENSE CATEGORIES
    =============================== */
    const loadCategories = async () => {
        try {
            const res = await axiosAuth.get(
                `${apiEndpoints.expense_categories}?action=get`
            );
            if (res.data.status === "success") {
                setCategories(res.data.data || []);
            }
        } catch (error) {
            console.error("Error loading categories:", error);
        }
    };

    /* ===============================
       LOAD STAFF (ONLY FOR SALARY)
    =============================== */
    const loadStaff = async () => {
        try {
            const res = await axiosAuth.get(
                `${apiEndpoints.staff_salary}?action=get_staff`
            );
            if (res.data.status === "success") {
                console.log("staff", res.data.data);
                setStaffList(res.data.data || []);
            }
        } catch (error) {
            console.error("Error loading staff:", error);
        }
    };

    /* ===============================
       RESET FORM WHEN ENTRY TYPE CHANGES
    =============================== */
    const handleEntryTypeChange = (newType) => {
        // Reset all dependent fields
        setForm({
            ...form,
            entry_type: newType,
            staff_guid: "",
            staff_name: "",
            expense_name: "",
            description: "",
            amount: "",
            mode: "cash",
            payment_date: ""
        });

        // Reset selection states
        setSelectedCategory(null);

        // Load staff if new type is salary
        if (newType === "salary") {
            loadStaff();
        }
    };

    /* ===============================
       HANDLE EXPENSE CATEGORY SELECTION
    =============================== */
    const handleCategorySelect = async (e, value) => {
        if (typeof value === "object" && value) {
            // Existing category selected
            setSelectedCategory(value);
            handleChange("expense_name", value.name);
        } else if (typeof value === "string" && value.trim()) {
            // New category entered
            try {
                const res = await axiosAuth.post(
                    `${apiEndpoints.expense_categories}?action=add`,
                    { name: value }
                );

                if (res.data.status === "success") {
                    const newItem = {
                        id: res.data.id,
                        name: value
                    };
                    setCategories(prev => [...prev, newItem]);
                    setSelectedCategory(newItem);
                    handleChange("expense_name", value);
                    showNotification("New category added successfully");
                }
            } catch (error) {
                console.error("Error adding category:", error);
                showNotification("Error adding category", "error");
            }
        } else {
            // Clear selection
            setSelectedCategory(null);
            handleChange("expense_name", "");
        }
    };

    /* ===============================
       SAVE EXPENSE TRANSACTION
    =============================== */
    const handleSaveExpense = async () => {
        // Validation
        console.log("data", form);
        if (!form.entry_type) {
            showNotification("Please select entry type", "error");
            return;
        }

        if (!form.amount || parseFloat(form.amount) <= 0) {
            showNotification("Please enter a valid amount", "error");
            return;
        }

        if (!form.payment_date) {
            showNotification("Please select payment date", "error");
            return;
        }

        // For salary entry type
        if (form.entry_type === "salary") {
            if (!form.staff_guid) {
                showNotification("Please select a staff member", "error");
                return;
            }
        } else {
            // For other expense types
            if (!form.expense_name) {
                showNotification("Please enter expense name", "error");
                return;
            }
        }

        setSaveLoading(true);
        try {
            const payload = {
                ...form,
                amount: parseFloat(form.amount)
            };

            // Remove empty fields
            Object.keys(payload).forEach(key => {
                if (payload[key] === "" || payload[key] === null) {
                    delete payload[key];
                }
            });

            const action = editingExpense ? "update" : "add";
            const url = editingExpense
                ? `${apiEndpoints.expense_transactions}?action=update&id=${editingExpense.id}`
                : `${apiEndpoints.expense_transactions}?action=add`;

            const response = await axiosAuth.post(url, payload);

            if (response.data.status === "success") {
                showNotification(
                    editingExpense
                        ? "Expense updated successfully"
                        : "Expense added successfully"
                );

                // Reload expenses and reset to first page
                setPage(1);
                loadExpenses();

                // Reset form and close modal
                handleCloseModal();
            } else {
                showNotification(response.data.message || "Failed to save expense", "error");
            }
        } catch (error) {
            console.error("Error saving expense:", error);
            showNotification("Error saving expense transaction", "error");
        } finally {
            setSaveLoading(false);
        }
    };

    /* ===============================
       DELETE EXPENSE TRANSACTION
    =============================== */
    const handleDeleteExpense = async (id) => {
        if (!window.confirm("Are you sure you want to delete this expense?")) {
            return;
        }

        try {
            const response = await axiosAuth.delete(
                `${apiEndpoints.expense_transactions}?action=delete&id=${id}`
            );

            if (response.data.status === "success") {
                showNotification("Expense deleted successfully");
                // Reload expenses
                loadExpenses();
            } else {
                showNotification(response.data.message || "Failed to delete expense", "error");
            }
        } catch (error) {
            console.error("Error deleting expense:", error);
            showNotification("Error deleting expense", "error");
        }
    };

    /* ===============================
       EDIT EXPENSE TRANSACTION
    =============================== */
    const handleEditExpense = (expense) => {
        setEditingExpense(expense);

        setForm({
            entry_type: expense.entry_type,
            staff_guid: expense.staff_guid || "",
            staff_name: expense.staff_name || "",
            expense_name: expense.expense_name || "",
            description: expense.description || "",
            amount: expense.amount,
            mode: expense.mode,
            transaction_type: expense.transaction_type,
            payment_date: expense.payment_date
        });

        if (expense.entry_type === "salary") {
            const staff = staffList.find(
                s => s.staff_guid === expense.staff_guid
            );
            setSelectedStaff(staff || null);
        }

        setOpenAdd(true);
    };

    /* ===============================
       RESET FORM AND CLOSE MODAL
    =============================== */
    const handleCloseModal = () => {
        setOpenAdd(false);
        setEditingExpense(null);
        setSelectedStaff(null);
        setSelectedCategory(null);
        setForm({
            entry_type: "",
            staff_guid: "",
            staff_name: "",
            expense_name: "",
            description: "",
            amount: "",
            mode: "cash",
            transaction_type: "out",
            payment_date: ""
        });
    };

    /* ===============================
       APPLY FILTERS
    =============================== */
    const applyFilters = () => {
        setPage(1); // Reset to first page when filters change
        loadExpenses();
    };

    /* ===============================
       CLEAR FILTERS
    =============================== */
    const clearFilters = () => {
        setTypeFilter("");
        setModeFilter("");
        setFrom("");
        setTo("");
        setPage(1); // Reset to first page
        loadExpenses();
    };

    // Calculate total pages
    const totalPages = Math.ceil(totalCount / rowsPerPage);

    useEffect(() => {
        loadCategories();
        loadExpenses();
    }, [page, rowsPerPage]); // Reload when page or rows per page changes

    useEffect(() => {
        if (form.entry_type === "salary") {
            loadStaff();
        }
    }, [form.entry_type]);

    return (
        <Box sx={{ p: 2 }}>
            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setNotification({ ...notification, open: false })}
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>

            {/* HEADER */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    alignItems: "center",
                    mb: 2,
                    gap: 2,
                }}
            >
                <Typography variant="h6" fontWeight={700}>
                    Expense Transactions
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<Add />}
                    sx={{ borderRadius: 3, px: 3 }}
                    onClick={() => setOpenAdd(true)}
                >
                    Add Expense
                </Button>
            </Box>

            {/* FILTERS */}
            <Paper sx={{
                p: 2,
                mb: 3,
                borderRadius: 3,
                background: "rgba(255,255,255,0.5)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(255,255,255,0.5)",
                boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
            }}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <TextField
                        type="date"
                        label="From"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        sx={{ width: { xs: "100%", md: 160 }, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />

                    <TextField
                        type="date"
                        label="To"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        sx={{ width: { xs: "100%", md: 160 }, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />

                    <TextField
                        select
                        label="Type"
                        size="small"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        sx={{ width: { xs: "100%", md: 160 }, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="salary">Staff Salary</MenuItem>
                        <MenuItem value="office">Office Expense</MenuItem>
                    </TextField>

                    <TextField
                        select
                        label="Mode"
                        size="small"
                        value={modeFilter}
                        onChange={(e) => setModeFilter(e.target.value)}
                        sx={{ width: { xs: "100%", md: 160 }, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="bank">Bank</MenuItem>
                        <MenuItem value="upi">UPI</MenuItem>
                    </TextField>

                    <Box sx={{ display: "flex", gap: 1, width: { xs: "100%", md: "auto" } }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={applyFilters}
                            sx={{ width: { xs: "100%", md: 160 }, borderRadius: 2 }}
                        >
                            Apply
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={clearFilters}
                            sx={{ width: { xs: "100%", md: 160 }, borderRadius: 2 }}
                        >
                            Clear
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* TABLE */}
            <Paper sx={{
                borderRadius: 3,
                overflow: "hidden",
                background: "rgba(255,255,255,0.5)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(255,255,255,0.5)",
                boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
            }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Mode</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Amount</TableCell>
                                        <TableCell>Txn</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {expenses.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                                <Typography color="textSecondary">
                                                    No expense transactions found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        expenses.map((expense) => (
                                            <TableRow key={expense.id} hover>
                                                <TableCell>{expense.id}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={expense.entry_type}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {expense.entry_type === "salary"
                                                        ? expense.staff_name
                                                        : expense.expense_name}
                                                </TableCell>
                                                <TableCell>{expense.mode}</TableCell>
                                                <TableCell>{expense.payment_date}</TableCell>
                                                <TableCell>₹{parseFloat(expense.amount).toFixed(2)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={expense.transaction_type.toUpperCase()}
                                                        color={statusColor(expense.transaction_type)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        color="primary"
                                                        size="small"
                                                        onClick={() => handleEditExpense(expense)}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleDeleteExpense(expense.id)}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* PAGINATION */}
                        <Box sx={{
                            p: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderTop: 1,
                            borderColor: 'divider'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" color="textSecondary">
                                    Total: {totalCount} records
                                </Typography>
                                <TextField
                                    select
                                    size="small"
                                    value={rowsPerPage}
                                    onChange={handleRowsPerPageChange}
                                    sx={{ width: 120 }}
                                >
                                    <MenuItem value={5}>5 per page</MenuItem>
                                    <MenuItem value={10}>10 per page</MenuItem>
                                    <MenuItem value={25}>25 per page</MenuItem>
                                    <MenuItem value={50}>50 per page</MenuItem>
                                </TextField>
                            </Box>

                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton
                                showLastButton
                                siblingCount={1}
                                boundaryCount={1}
                            />

                            <Typography variant="body2" color="textSecondary">
                                Page {page} of {totalPages}
                            </Typography>
                        </Box>
                    </>
                )}
            </Paper>

            {/* ADD/EDIT EXPENSE MODAL */}
            <Modal open={openAdd} onClose={handleCloseModal}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", sm: 450 },
                        maxHeight: "90vh",
                        overflow: "auto",
                        bgcolor: "white",
                        p: 3,
                        borderRadius: 3,
                        boxShadow: 5,
                    }}
                >
                    <Typography variant="h6" fontWeight={700} mb={2}>
                        {editingExpense ? "Edit Expense" : "Add Expense"}
                    </Typography>

                    {/* ENTRY TYPE */}
                    <TextField
                        select
                        fullWidth
                        label="Entry Type"
                        size="small"
                        sx={{ mb: 2 }}
                        value={form.entry_type}
                        onChange={(e) => handleEntryTypeChange(e.target.value)}
                    >
                        <MenuItem value="salary">Staff Salary</MenuItem>
                        <MenuItem value="office">Office Expense</MenuItem>
                    </TextField>

                    {/* STAFF SALARY FLOW */}
                    {form.entry_type === "salary" ? (
                        <Autocomplete
                            options={staffList}
                            value={selectedStaff}
                            getOptionLabel={(option) => option.staff_name || ""}
                            isOptionEqualToValue={(option, value) =>
                                option.staff_guid === value.staff_guid
                            }
                            onChange={(e, value) => {
                                setSelectedStaff(value);
                                if (value) {
                                    setForm(prev => ({
                                        ...prev,
                                        staff_guid: value.staff_guid,
                                        staff_name: value.staff_name,
                                        amount: value.salary || ""
                                    }));
                                } else {
                                    setForm(prev => ({
                                        ...prev,
                                        staff_guid: "",
                                        staff_name: "",
                                        amount: ""
                                    }));
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Staff Name"
                                    size="small"
                                    required
                                    sx={{ mb: 2 }}
                                />
                            )}
                        />

                    ) : form.entry_type ? (
                        <>
                            {/* EXPENSE NAME */}
                            <Autocomplete
                                freeSolo
                                options={categories}
                                value={selectedCategory}
                                getOptionLabel={(option) =>
                                    typeof option === "string" ? option : option.name
                                }
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                onChange={handleCategorySelect}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Type and Enter To Save Expence Name"
                                        size="small"
                                        sx={{ mb: 2 }}
                                        required
                                    />
                                )}
                            />

                            <TextField
                                fullWidth
                                label="Description"
                                size="small"
                                multiline
                                rows={2}
                                sx={{ mb: 2 }}
                                value={form.description}
                                onChange={(e) =>
                                    handleChange("description", e.target.value)
                                }
                            />
                        </>
                    ) : null}

                    {/* AMOUNT */}
                    <TextField
                        fullWidth
                        label="Amount"
                        size="small"
                        type="number"
                        sx={{ mb: 2 }}
                        value={form.amount}
                        onChange={(e) => handleChange("amount", e.target.value)}
                        required
                        InputProps={{
                            startAdornment: "₹"
                        }}
                    />

                    {/* PAYMENT MODE */}
                    <TextField
                        select
                        fullWidth
                        label="Payment Mode"
                        size="small"
                        sx={{ mb: 2 }}
                        value={form.mode}
                        onChange={(e) => handleChange("mode", e.target.value)}
                    >
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="bank">Bank</MenuItem>
                        <MenuItem value="upi">UPI</MenuItem>
                    </TextField>

                    {/* TRANSACTION TYPE */}
                    <TextField
                        select
                        fullWidth
                        label="Transaction Type"
                        size="small"
                        sx={{ mb: 2 }}
                        value={form.transaction_type}
                        onChange={(e) => handleChange("transaction_type", e.target.value)}
                    >
                        <MenuItem value="out">Payment (Out)</MenuItem>
                        <MenuItem value="in">Refund (In)</MenuItem>
                    </TextField>

                    {/* PAYMENT DATE */}
                    <TextField
                        type="date"
                        fullWidth
                        label="Payment Date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 3 }}
                        value={form.payment_date}
                        onChange={(e) =>
                            handleChange("payment_date", e.target.value)
                        }
                        required
                    />

                    <Box sx={{ display: "flex", gap: 2 }}>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleCloseModal}
                            disabled={saveLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleSaveExpense}
                            disabled={saveLoading}
                            startIcon={saveLoading ? <CircularProgress size={20} /> : null}
                        >
                            {saveLoading ? "Saving..." : (editingExpense ? "Update" : "Save")}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default ExpenseEntry;